import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';

import {
  getCoursesState,
  getEnrolmentCoursesForUsers,
  getNonEnrolmentCoursesForUsers,
  loadMoreEnrolmentCoursesForUsers,
  loadMoreNonEnrolmentCoursesForUsers,
  resetUserEnrolmentCoursesData,
  resetUserNonEnrolmentCoursesData,
} from '../../../store/V2/CoursesSlice';
import {
  getScheduleState,
  getUserSchedulesAsUser,
  IScheduleShortEvent,
  resetUserScheduleData,
} from '../../../store/V2/ScheduleSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import { getAuthState } from '../../../store/AuthSlice';
import { TPTextField } from '../../../components/Inputs';
import CourseCard from './components/CourseCard';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import UpcomingClassCard from './components/UpcomingClassCard';

type TState = {
  enrolmentFilterData: {
    limit: number;
    offset: number;
    enrolment: 'ENROLLED';
    deviceTimezone: string;
  };
  nonEnrolmentFilterData: {
    limit: number;
    offset: number;
    enrolment: 'UNENROLLED';
    deviceTimezone: string;
  };
  searchText: string;
};

const searchInputProps: TextFieldProps = {
  fullWidth: true,
  placeholder: 'Search Courses',
  sx: {
    maxWidth: '666px',
    '& .MuiInputBase-input': {
      fontWeight: 400,
      fontSize: '18px',
      textAlign: 'center',
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      top: '50%',
      left: 'calc(50% - 90px)', // Adjust based on the icon size
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
    },
  },
  InputProps: {
    sx: {
      backgroundColor: TP_COLORS.white,
      borderRadius: '35px',
      boxShadow: '0px 14px 40px 0px #6E9DB22E',
    },
  },
};

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'text',
      title: 'Courses',
      highlight: true,
    },
  ];

  return options;
};

const filterOutExpiredUpcomingClasses = (item: IScheduleShortEvent) => {
  return (
    moment(item.start) > moment() ||
    (moment() >= moment(item.start) && moment() < moment(item.end))
  );
};

const sortUpcomingClasses = (a: IScheduleShortEvent, b: IScheduleShortEvent) =>
  new Date(a.start).getTime() - new Date(b.start).getTime();

const defaultDeviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const initialEnrolmentFilterData: TState['enrolmentFilterData'] = {
  limit: 30,
  offset: 0,
  enrolment: 'ENROLLED',
  deviceTimezone: defaultDeviceTimezone,
};

const initialNonEnrolmentFilterData: TState['nonEnrolmentFilterData'] = {
  limit: 30,
  offset: 0,
  enrolment: 'UNENROLLED',
  deviceTimezone: defaultDeviceTimezone,
};

const initialState: TState = {
  enrolmentFilterData: { ...initialEnrolmentFilterData },
  nonEnrolmentFilterData: { ...initialNonEnrolmentFilterData },
  searchText: '',
};

const CoursesPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const authState = useAppSelector(getAuthState);
  const coursesState = useAppSelector(getCoursesState);
  const scheduleState = useAppSelector(getScheduleState);

  // State
  const [state, setState] = useState(initialState);

  const {
    userEnrolmentCoursesData,
    userNonEnrolmentCoursesData,
  } = coursesState;
  const authUser = authState.user;
  const breadcrumbOptions = constructBreadcrumbOptions();
  const upcomingClasses =
    scheduleState.userScheduleData &&
    scheduleState.userScheduleData.list &&
    scheduleState.userScheduleData.list.length
      ? scheduleState.userScheduleData.list
          .filter(filterOutExpiredUpcomingClasses)
          .sort(sortUpcomingClasses)
          .slice(0, 4)
      : [];

  useEffect(() => {
    if (authUser && authUser.role.name !== 'Guest') {
      dispatch(
        getUserSchedulesAsUser({
          userId: authUser._id,
          startDate: moment().startOf('day').toISOString(),
          endDate: moment().add(1, 'month').endOf('day').toISOString(),
        })
      );
    }

    dispatch(
      getEnrolmentCoursesForUsers({
        ...state.enrolmentFilterData,
        searchText: state.searchText,
      })
    );

    dispatch(
      getNonEnrolmentCoursesForUsers({
        ...state.nonEnrolmentFilterData,
        searchText: state.searchText,
      })
    );

    return () => {
      dispatch(resetUserEnrolmentCoursesData());
      dispatch(resetUserNonEnrolmentCoursesData());
      dispatch(resetUserScheduleData());
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
      enrolmentFilterData: { ...initialEnrolmentFilterData },
      nonEnrolmentFilterData: { ...initialNonEnrolmentFilterData },
    }));

    dispatch(
      getEnrolmentCoursesForUsers({
        ...initialEnrolmentFilterData,
        searchText: value,
      })
    );

    dispatch(
      getNonEnrolmentCoursesForUsers({
        ...initialNonEnrolmentFilterData,
        searchText: value,
      })
    );
  };

  const handleEnrolmentLoadMore = () => {
    if (userEnrolmentCoursesData) {
      const newFilterData = {
        ...state.enrolmentFilterData,
        offset: userEnrolmentCoursesData.list.length,
        searchText: state.searchText,
      };

      setState((prevState) => ({
        ...prevState,
        enrolmentFilterData: newFilterData,
      }));

      dispatch(
        loadMoreEnrolmentCoursesForUsers({
          ...newFilterData,
          searchText: state.searchText,
        })
      );
    }
  };

  const handleNonEnrolmentLoadMore = () => {
    if (userNonEnrolmentCoursesData) {
      const newFilterData = {
        ...state.nonEnrolmentFilterData,
        offset: userNonEnrolmentCoursesData.list.length,
        searchText: state.searchText,
      };

      setState((prevState) => ({
        ...prevState,
        nonEnrolmentFilterData: newFilterData,
      }));

      dispatch(
        loadMoreNonEnrolmentCoursesForUsers({
          ...newFilterData,
          searchText: state.searchText,
        })
      );
    }
  };

  return (
    <>
      <Box
        sx={{
          background: `url('${process.env.PUBLIC_URL}/images/backgrounds/bg-courses-grid.png') 0px -15px`,
          paddingBottom: '90px',
        }}
      >
        <Container maxWidth="lg">
          <div style={{ padding: `30px 0 ${isXsScreen ? '15px' : '0'} 0` }}>
            <Breadcrumbs
              options={breadcrumbOptions}
              textColor="#647488"
              showDropDown={isXsScreen}
            />
          </div>
          <section style={{ textAlign: 'center' }}>
            <div>
              <Typography
                component="h1"
                color="#006C9C"
                fontWeight={800}
                fontSize="80px"
                margin={0}
              >
                Courses
              </Typography>
            </div>
            <Stack
              direction="row"
              justifyContent="center"
              style={{ marginBottom: '50px', marginTop: '-10px' }}
            >
              <Typography
                color="#006C9C"
                fontWeight={500}
                maxWidth="750px"
                fontSize="18px"
                lineHeight="21px"
              >
                Meet our incredible team of experts with decades of experience!
                They&apos;re here to make your academic journey a success,
                offering guidance and support along the way.
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="center">
              <TPTextField
                {...searchInputProps}
                InputProps={{
                  ...searchInputProps.InputProps,
                  startAdornment: state.searchText.length ? undefined : (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                name="searchText"
                value={state.searchText}
                onChange={handleInputChange}
              />
            </Stack>
          </section>
        </Container>
      </Box>
      <Container maxWidth="lg" style={{ marginTop: '-65px' }}>
        <Grid component="section" container spacing={2}>
          {upcomingClasses.length ? (
            <>
              <Grid item xs={12}>
                <Divider textAlign="left">
                  <Typography variant="h6">Upcoming Classes</Typography>
                </Divider>
              </Grid>
              {upcomingClasses.map((item) => (
                <Grid
                  key={`upcoming-class-card-${item._id}`}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                >
                  <UpcomingClassCard classData={item} />
                </Grid>
              ))}
            </>
          ) : null}
          {userEnrolmentCoursesData &&
          (userEnrolmentCoursesData.list || []).length ? (
            <>
              <Grid item xs={12}>
                <Divider textAlign="left">
                  <Typography variant="h6">My Courses</Typography>
                </Divider>
              </Grid>
              {userEnrolmentCoursesData.list.map((item) => {
                return (
                  <Grid
                    key={`course-${item._id}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                  >
                    <CourseCard enrolled course={item} hideAmount />
                  </Grid>
                );
              })}
              {userEnrolmentCoursesData &&
                (userEnrolmentCoursesData.totalDocs || 0) >
                  userEnrolmentCoursesData.list.length && (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="center">
                      <LoadingButton
                        loading={coursesState.status === 'loading'}
                        disabled={coursesState.status === 'loading'}
                        onClick={handleEnrolmentLoadMore}
                      >
                        Load More
                      </LoadingButton>
                    </Stack>
                  </Grid>
                )}
              <Grid item xs={12}>
                <Divider textAlign="left">
                  <Typography variant="h6">Other Courses</Typography>
                </Divider>
              </Grid>
            </>
          ) : null}
          {userNonEnrolmentCoursesData &&
          (userNonEnrolmentCoursesData.list || []).length
            ? userNonEnrolmentCoursesData.list.map((item) => {
                return (
                  <Grid
                    key={`course-${item._id}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                  >
                    <CourseCard course={item} />
                  </Grid>
                );
              })
            : null}
          {userNonEnrolmentCoursesData &&
            (userNonEnrolmentCoursesData.totalDocs || 0) >
              userNonEnrolmentCoursesData.list.length && (
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    loading={coursesState.status === 'loading'}
                    disabled={coursesState.status === 'loading'}
                    onClick={handleNonEnrolmentLoadMore}
                  >
                    Load More
                  </LoadingButton>
                </Stack>
              </Grid>
            )}
          {userNonEnrolmentCoursesData &&
          userNonEnrolmentCoursesData.list &&
          userNonEnrolmentCoursesData.list.length < 1 &&
          userEnrolmentCoursesData &&
          userEnrolmentCoursesData.list &&
          userEnrolmentCoursesData.list.length < 1 &&
          coursesState.status !== 'loading' ? (
            <Grid item xs={12}>
              <Typography variant="h6" textAlign="center">
                No courses found
              </Typography>
            </Grid>
          ) : null}
          {coursesState.status === 'loading' &&
          (!userNonEnrolmentCoursesData ||
            !userEnrolmentCoursesData ||
            !scheduleState.userScheduleData) ? (
            <Grid item xs={12}>
              <BasicLoader />
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </>
  );
};

export default CoursesPage;
